import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import { ScrollText, CreditCard, ShieldCheck } from "lucide-react";

export default function AuthPage({ mode }) {
 const isRegister = mode === "register";
 const { login, register } = useAuth();
 const navigate = useNavigate();
 const location = useLocation();
 const [form, setForm] = useState({ name: "", email: "", password: "" });
 const [err, setErr] = useState("");
 const [loading, setLoading] = useState(false);

 // When the user clicked a paid plan on /pricing while logged out, we get
 // sent here with ?next=/pricing. Show a banner so they understand the
 // flow is account-first, not that they've lost their plan selection.
 const nextPath = new URLSearchParams(location.search).get("next") || "/dashboard";
 const isCheckoutFlow = nextPath.startsWith("/pricing") || nextPath.startsWith("/billing");

 const submit = async (e) => {
 e.preventDefault();
 setErr("");
 setLoading(true);
 try {
 if (isRegister) {
 await register(form.name, form.email, form.password);
 } else {
 await login(form.email, form.password);
 }
 const next = new URLSearchParams(location.search).get("next") || "/dashboard";
 navigate(next);
 } catch (e) {
 const d = e.response?.data?.detail;
 const msg = Array.isArray(d) ? (d[0]?.msg || "Validation failed") : (typeof d === "string" ? d : "Something went wrong");
 setErr(msg);
 } finally {
 setLoading(false);
 }
 };

 return (
 <div className="min-h-screen paper-grain">
 <Navbar minimal />
 <div className="max-w-md mx-auto px-6 pt-16 pb-24">
 {isCheckoutFlow && (
 <div
 className="card border-l-4 border-l-[#1B6C62] bg-[#E6F5F2]/40 mb-5 py-4 px-5"
 data-testid="checkout-flow-banner"
 >
 <div className="flex items-start gap-2.5">
 <CreditCard size={16} strokeWidth={1.75} className="text-[#1B6C62] mt-0.5 flex-shrink-0" />
 <div className="text-[13px] text-[#1A1A1A] leading-snug font-serif">
 <div className="font-medium text-[#0D2B52] mb-1">Account first, then payment</div>
 <p>
 {isRegister
 ? "We create your free TenantTrail account first so your case file is ready. After you sign up we'll take you back to pricing to confirm your plan and process payment with Stripe."
 : "Sign in to continue. We'll take you back to pricing to confirm your plan and process payment with Stripe."}
 </p>
 </div>
 </div>
 </div>
 )}
 <div className="card fade-up">
 <div className="flex items-center gap-2 mb-6">
 <ScrollText size={20} strokeWidth={1.5} className="text-[#0D2B52]" />
 <span className="label-mono">{isRegister ? "Create your case file" : "Welcome back"}</span>
 </div>
 <h1 className="font-serif text-3xl text-[#1A1A1A] mb-8">
 {isRegister ? "Start your trail" : "Sign in"}
 </h1>

 <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
 {isRegister && (
 <div>
 <label className="label-mono block mb-1.5">Your name</label>
 <input
 type="text" required
 value={form.name}
 onChange={(e) => setForm({...form, name: e.target.value })}
 className="input-field"
 data-testid="auth-name"
 placeholder="Jane Tenant"
 />
 </div>
 )}
 <div>
 <label className="label-mono block mb-1.5">Email</label>
 <input
 type="email" required
 value={form.email}
 onChange={(e) => setForm({...form, email: e.target.value })}
 className="input-field"
 data-testid="auth-email"
 placeholder="you@example.com"
 />
 </div>
 <div>
 <label className="label-mono block mb-1.5">Password</label>
 <input
 type="password" required minLength={8}
 value={form.password}
 onChange={(e) => setForm({...form, password: e.target.value })}
 className="input-field"
 data-testid="auth-password"
 placeholder={isRegister ? "Min 8 characters" : "••••••••"}
 />
 </div>

 {err && (
 <div className="text-sm text-[#B35D4C] bg-[#B35D4C]/5 border border-[#B35D4C]/20 px-3 py-2 rounded-sm" data-testid="auth-error">
 {err}
 </div>
 )}

 <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="auth-submit">
 {loading ? "Please wait…" : isRegister ? "Create account" : "Sign in"}
 </button>
 {isRegister && (
 <p className="text-[11.5px] text-[#6B7280] text-center leading-snug mt-3" data-testid="auth-trust-link-wrapper">
 <ShieldCheck size={11} strokeWidth={1.75} className="inline-block -mt-0.5 mr-1 text-[#1B6C62]" />
 Wondering how we keep your documents safe?{" "}
 <Link to="/trust" className="text-[#0D2B52] hover:underline" data-testid="auth-trust-link">See our trust and security page</Link>.
 </p>
 )}
 </form>

 <div className="mt-6 text-sm text-[#6B7280] text-center">
 {isRegister ? (
 <>Already have an account? <Link to="/login" className="text-[#0D2B52] hover:underline" data-testid="auth-switch">Sign in</Link></>
 ) : (
 <>New here? <Link to="/register" className="text-[#0D2B52] hover:underline" data-testid="auth-switch">Create an account</Link></>
 )}
 </div>
 </div>
 {isCheckoutFlow && (
 <p
 className="text-[11px] text-[#6B7280] mt-4 text-center leading-snug px-2"
 data-testid="checkout-microcopy"
 >
 Cancel anytime. You&rsquo;ll be billed your chosen plan price until you cancel. No refunds for change of mind - your rights under the Australian Consumer Law still apply.{" "}
 <Link to="/refunds" className="underline hover:text-[#0D2B52]" data-testid="checkout-refunds-link">
 Refund terms
 </Link>
 .
 </p>
 )}
 </div>
 </div>
 );
}
